<template>
  <div class="order-wait-page">
    <div v-if="loading" class="loader-block">
      <b-spinner small class="mr-2"></b-spinner>
      <span>Загружаем заявку…</span>
    </div>

    <div v-else-if="!order" class="empty-block">
      <div class="empty-ic">🚫</div>
      <div class="empty-title">Заявка не найдена</div>
      <div class="empty-sub">Возможно, у вас нет доступа к этому ордеру.</div>
      <b-button class="mt-3" variant="outline-light" @click="$router.push('/buy/BUY')">
        ← Создать ордер
      </b-button>
    </div>

    <template v-else>
      <!-- Top banner: status + countdown -->
      <div class="status-banner" :class="bannerClass">
        <div class="banner-ic">{{ bannerIcon }}</div>
        <div class="banner-content">
          <div class="banner-title">{{ bannerTitle }}</div>
          <div class="banner-sub" v-if="bannerSub">{{ bannerSub }}</div>
        </div>
        <div class="countdown-block" v-if="showCountdown">
          <div class="countdown-lbl">Действительно ещё</div>
          <div class="countdown-val">{{ formatRemaining(remainingMs) }}</div>
        </div>
      </div>

      <!-- Order detail card -->
      <div class="ord-card">
        <div class="ord-head">
          <span class="ord-id">Ордер #{{ order.orderId }}</span>
          <span class="ord-type">{{ order.exType === 'BUY' ? 'Покупка' : 'Продажа' }}</span>
        </div>

        <div class="kv-row">
          <div class="kv-k">Списано с баланса</div>
          <div class="kv-v">{{ formatAmount(order.sellAmount, order.sellAsset) }}</div>
        </div>
        <div class="kv-row">
          <div class="kv-k">К получению</div>
          <div class="kv-v hilite">{{ formatAmount(order.buyAmount, order.buyAsset) }}<span v-if="order.buyNetwork"> ({{ order.buyNetwork }})</span></div>
        </div>
        <div class="kv-row">
          <div class="kv-k">Курс сделки</div>
          <div class="kv-v">1 {{ cryptoSide }} = {{ formatRate(order.lockedPrice) }} USD</div>
        </div>
        <div class="kv-row" v-if="order.exType === 'BUY'">
          <div class="kv-k">Стоимость {{ formatAmount(order.buyAmount, order.buyAsset) }}</div>
          <div class="kv-v">{{ formatAmount(cryptoCostUsd, 'USD') }}</div>
        </div>
        <div class="kv-row" v-if="order.companyFeeUsd != null">
          <div class="kv-k">Комиссия Payton</div>
          <div class="kv-v">
            <template v-if="order.exType === 'SELL'">{{ formatAmount(sellFeeCrypto, order.sellAsset) }}</template>
            <template v-else>{{ formatAmount(order.companyFeeUsd, 'USD') }}</template>
          </div>
        </div>
        <!-- Сетевой сбор — только для покупки (on-chain отправка крипты).
             Для продажи это банковский перевод, сбор не взимается. -->
        <div class="kv-row" v-if="order.exType === 'BUY' && order.networkFeeEstUsd != null && order.networkFeeEstUsd > 0">
          <div class="kv-k">Сетевой сбор (ориент.)</div>
          <div class="kv-v">~{{ formatAmount(order.networkFeeEstUsd, 'USD') }}</div>
        </div>
        <div class="kv-row">
          <div class="kv-k">Создан</div>
          <div class="kv-v">{{ formatDateTime(order.lockedAt) }}</div>
        </div>
        <div class="kv-row" v-if="order.expiresAt">
          <div class="kv-k">Действует до</div>
          <div class="kv-v">{{ formatDateTime(order.expiresAt) }}</div>
        </div>
      </div>

      <!-- Reconfirm gate: показываем когда рынок ушёл >0.3% и трейдер
           попросил клиента подтвердить новую цену. Pretty side-by-side
           comparison: было / стало + большие кнопки Accept / Reject. -->
      <div v-if="effectiveStatus === 'AWAITING_RECONFIRM'" class="reconfirm-card">
        <div class="reconfirm-title">Курс изменился</div>
        <div class="reconfirm-sub">
          С момента создания заявки рыночная цена ушла больше допустимых 0.3%.
          Подтвердите новую цену или отмените заявку — резерв вернётся на баланс.
        </div>

        <div class="rate-compare">
          <div class="rate-box rate-old">
            <div class="rate-lbl">Было</div>
            <div class="rate-val">{{ formatRate(order.lockedPrice) }} USD</div>
            <div class="rate-sub">за 1 {{ cryptoSide }}</div>
          </div>
          <div class="rate-arrow">→</div>
          <div class="rate-box rate-new">
            <div class="rate-lbl">Стало</div>
            <div class="rate-val">{{ formatRate(order.reconfirmRate) }} USD</div>
            <div class="rate-sub">за 1 {{ cryptoSide }} · {{ driftLabel }}</div>
          </div>
        </div>

        <div v-if="order.exType === 'BUY'" class="reconfirm-note-line">
          Вы получите те же <b>{{ formatAmount(order.buyAmount, order.buyAsset) }}</b>.
          Спишется ≈ <b>{{ formatAmount(reconfirmNewDebitUsd, 'USD') }}</b>
          (было {{ formatAmount(order.sellAmount, 'USD') }}).
        </div>

        <div v-if="reconfirmRemainingMs > 0" class="reconfirm-countdown">
          У вас есть <b>{{ formatRemaining(reconfirmRemainingMs) }}</b> на ответ.
          После — заявка отменится автоматически.
        </div>
        <div v-else class="reconfirm-countdown warn">
          Время на ответ истекло — пожалуйста, нажмите «Отменить»,
          чтобы вернуть резерв.
        </div>

        <div class="reconfirm-actions">
          <b-button class="reject-btn" variant="outline-light" :disabled="reconfirmBusy" @click="submitReconfirm(false)">
            ✕ Отменить заявку
          </b-button>
          <b-button class="accept-btn cta-btn" variant="primary" :disabled="reconfirmBusy || reconfirmRemainingMs <= 0" @click="submitReconfirm(true)">
            <b-spinner small v-if="reconfirmBusy" />
            <span v-else>✓ Принять новую цену</span>
          </b-button>
        </div>
      </div>

      <!-- Note about what's next -->
      <div class="note">
        <b-icon icon="info-circle" class="mr-2"></b-icon>
        <span v-if="effectiveStatus === 'PENDING'">
          Заявка передана трейдеру. Курс зафиксирован — резерв на вашем балансе.
          Как только трейдер начнёт исполнение, статус обновится. Если в течение
          {{ ttlMinutesText }} ничего не произойдёт — резерв вернётся автоматически.
        </span>
        <span v-else-if="effectiveStatus === 'AWAITING_RECONFIRM'">
          Рыночная цена ушла больше допустимого. Ждём вашего подтверждения новой цены.
        </span>
        <span v-else-if="effectiveStatus === 'EXECUTING'">
          Трейдер выполняет заявку на партнёрской бирже.
        </span>
        <span v-else-if="effectiveStatus === 'COMPLETED'">
          Заявка успешно исполнена.
        </span>
        <span v-else-if="effectiveStatus === 'EXPIRED'">
          Заявка истекла — резерв возвращён на баланс. Создайте новую с актуальной ценой.
        </span>
        <span v-else-if="effectiveStatus === 'CANCELLED'">
          Заявка отменена — резерв возвращён на баланс.
        </span>
        <span v-else-if="effectiveStatus === 'REJECTED'">
          Заявка отклонена оператором — резерв возвращён на баланс.
        </span>
      </div>

      <!-- Settlement-блок: показываем когда есть p_settlement-строка.
           Для BUY ведёт хронику on-chain отправки (PENDING → BROADCAST
           → CONFIRMED), для SELL — банковского перевода USD клиенту. -->
      <div v-if="settlement" class="settlement-card" :class="settlementBannerClass">
        <div class="settlement-head">
          <div class="settlement-ic">{{ settlementIcon }}</div>
          <div class="settlement-titles">
            <div class="settlement-title">{{ settlementTitle }}</div>
            <div class="settlement-sub">{{ settlementSub }}</div>
          </div>
        </div>

        <!-- BUY: tx_hash + actual gas -->
        <template v-if="settlement.settleKind === 'ONCHAIN'">
          <div v-if="settlement.txHash" class="kv-row">
            <div class="kv-k">tx_hash</div>
            <div class="kv-v mono">
              {{ settlement.txHash }}
              <a v-if="explorerUrl" :href="explorerUrl" target="_blank" rel="noopener" class="explorer-link">
                открыть ↗
              </a>
            </div>
          </div>
          <div v-if="settlement.actualNetworkFeeUsd != null" class="kv-row">
            <div class="kv-k">Сетевой сбор фактический</div>
            <div class="kv-v">{{ formatAmount(settlement.actualNetworkFeeUsd, 'USD') }}</div>
          </div>
          <div v-if="settlement.broadcastAt" class="kv-row">
            <div class="kv-k">Отправлено</div>
            <div class="kv-v">{{ formatDateTime(settlement.broadcastAt) }}</div>
          </div>
          <div v-if="settlement.confirmedAt" class="kv-row">
            <div class="kv-k">Подтверждено</div>
            <div class="kv-v">{{ formatDateTime(settlement.confirmedAt) }}</div>
          </div>
        </template>

        <!-- SELL: банковский перевод USD на счёт клиента -->
        <template v-else>
          <div class="kv-row">
            <div class="kv-k">Сумма к переводу</div>
            <div class="kv-v hilite">{{ formatAmount(order.buyAmount, 'USD') }}</div>
          </div>
          <div v-if="settlement.bankReference" class="kv-row">
            <div class="kv-k">Референс перевода</div>
            <div class="kv-v mono">{{ settlement.bankReference }}</div>
          </div>
          <div v-if="settlement.bankReference" class="kv-hint">
            Референс — это уникальный номер вашего банковского перевода
            (SWIFT / MT103 / номер платёжного поручения). По нему банк и
            наша поддержка могут найти именно этот платёж, если возникнут
            вопросы по зачислению.
          </div>
          <div v-if="settlement.confirmedAt || settlement.broadcastAt" class="kv-row">
            <div class="kv-k">Оформлено</div>
            <div class="kv-v">{{ formatDateTime(settlement.confirmedAt || settlement.broadcastAt) }}</div>
          </div>
          <div v-if="settlement.bankReceiptPath" class="kv-row">
            <div class="kv-k">Платёжка</div>
            <div class="kv-v">
              <a href="javascript:void(0)" class="explorer-link" @click="downloadBankReceipt">Скачать ↓</a>
            </div>
          </div>
        </template>
      </div>

      <!-- Audit timeline: что происходило с ордером. Все события из
           p_order_event в хронологическом порядке. -->
      <div v-if="events.length" class="timeline-card">
        <div class="timeline-title">История заявки</div>
        <div class="timeline-list">
          <div v-for="event in visibleEvents" :key="event.eventId" class="timeline-item">
            <div class="timeline-dot" :class="dotClassFor(event)">{{ iconFor(event) }}</div>
            <div class="timeline-body">
              <div class="timeline-row">
                <div class="timeline-label">{{ labelFor(event) }}</div>
                <div class="timeline-time">{{ formatDateTime(event.occurredAt) }}</div>
              </div>
              <div v-if="payloadLines(event).length" class="timeline-payload">
                <div v-for="(line, idx) in payloadLines(event)" :key="idx">{{ line }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="actions">
        <b-button variant="outline-light" @click="$router.push('/userwallet')">
          К балансам
        </b-button>
        <!-- Cancel доступен только пока ордер PENDING (трейдер ещё не
             начал). После start-execution — поздно. -->
        <b-button
          v-if="effectiveStatus === 'PENDING'"
          variant="outline-danger"
          class="ml-2"
          :disabled="cancelBusy"
          @click="confirmCancel"
        >Отменить заявку</b-button>
        <b-button v-if="effectiveStatus === 'EXPIRED'" variant="primary" class="cta-btn ml-2" @click="$router.push('/buy/' + order.exType)">
          Создать новую заявку
        </b-button>
        <b-button v-else variant="primary" class="cta-btn ml-2" @click="refresh">
          Обновить
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '@/config/config'

export default {
  name: 'OrderWait',
  data() {
    return {
      order: null,
      loading: true,
      now: Date.now(),     // живёт через setInterval для countdown'а
      tickerHandle: null,
      pollHandle: null,
      reconfirmBusy: false,
      cancelBusy: false,
      events: [],
      settlement: null,
    }
  },
  computed: {
    orderId() {
      return Number(this.$route.params.orderId || 0)
    },
    visibleEvents() {
      // Прячем от клиента compliance-события (KYW_CHECKED) — это
      // внутренний audit-trail, клиенту неинтересен факт проверки
      // адреса. Backend всё равно шлёт их в JSON, фильтруем на клиенте.
      const HIDDEN = ['KYW_CHECKED']
      return (this.events || []).filter((e) => !HIDDEN.includes(e.eventType))
    },
    effectiveStatus() {
      // Backend проецирует EXPIRED через EffectiveStatus — берём его,
      // если есть, иначе сырой status.
      return (this.order && (this.order.effectiveStatus || this.order.status)) || ''
    },
    expiresAtMs() {
      const raw = this.order && this.order.expiresAt
      if (!raw) return 0
      return Date.parse(raw)
    },
    remainingMs() {
      return Math.max(0, this.expiresAtMs - this.now)
    },
    showCountdown() {
      return this.effectiveStatus === 'PENDING' && this.expiresAtMs > 0
    },
    bannerIcon() {
      return ({
        PENDING:            '⏳',
        AWAITING_RECONFIRM: '🔄',
        EXECUTING:          '⚡',
        COMPLETED:          '✓',
        EXPIRED:            '⌛',
        CANCELLED:          '✕',
        REJECTED:           '⛔',
      })[this.effectiveStatus] || '•'
    },
    bannerTitle() {
      return ({
        PENDING:            'Заявка в очереди',
        AWAITING_RECONFIRM: 'Требуется подтверждение новой цены',
        EXECUTING:          'Заявка исполняется',
        COMPLETED:          'Заявка исполнена',
        EXPIRED:            'Заявка истекла',
        CANCELLED:          'Заявка отменена',
        REJECTED:           'Заявка отклонена',
      })[this.effectiveStatus] || this.effectiveStatus
    },
    bannerSub() {
      if (this.effectiveStatus === 'PENDING') return 'Курс зафиксирован. Резерв на балансе действует до истечения TTL.'
      if (this.effectiveStatus === 'EXPIRED') return 'Резерв возвращён на баланс.'
      return ''
    },
    bannerClass() {
      return ({
        PENDING:            'banner-pending',
        AWAITING_RECONFIRM: 'banner-warn',
        EXECUTING:          'banner-info',
        COMPLETED:          'banner-ok',
        EXPIRED:            'banner-muted',
        CANCELLED:          'banner-muted',
        REJECTED:           'banner-no',
      })[this.effectiveStatus] || ''
    },
    cryptoSide() {
      if (!this.order) return ''
      return this.order.exType === 'BUY' ? this.order.buyAsset : this.order.sellAsset
    },
    // BUY: стоимость получаемой крипты в USD (база) = кол-во × курс.
    // «Списано» = база + комиссия + сеть, поэтому эта строка делает
    // разбивку сходящейся на глаз.
    cryptoCostUsd() {
      if (!this.order || this.order.exType !== 'BUY') return 0
      return (Number(this.order.buyAmount) || 0) * (Number(this.order.lockedPrice) || 0)
    },
    // SELL: комиссия в самом крипто-активе. Берём нативное поле
    // companyFeeCrypto (источник истины); деление на курс — фолбэк для
    // ордеров, созданных до появления колонки.
    sellFeeCrypto() {
      if (!this.order || this.order.exType !== 'SELL') return 0
      if (this.order.companyFeeCrypto != null) return Number(this.order.companyFeeCrypto)
      const rate = Number(this.order.lockedPrice) || 0
      if (!rate) return 0
      return (Number(this.order.companyFeeUsd) || 0) / rate
    },
    reconfirmExpiresMs() {
      const raw = this.order && this.order.reconfirmExpiresAt
      if (!raw) return 0
      return Date.parse(raw)
    },
    reconfirmRemainingMs() {
      const t = this.reconfirmExpiresMs
      if (!t) return 0
      return Math.max(0, t - this.now)
    },
    driftLabel() {
      const oldRate = Number(this.order && this.order.lockedPrice) || 0
      const newRate = Number(this.order && this.order.reconfirmRate) || 0
      if (!oldRate || !newRate) return ''
      const drift = ((newRate - oldRate) / oldRate) * 100
      const sign = drift > 0 ? '+' : ''
      return sign + drift.toFixed(2) + '%'
    },
    // BUY: при подтверждении объём крипты остаётся тем же (что обещали),
    // а меняется USD-списание. Прикидываем новое списание по новому
    // курсу: база + комиссия (по прежней ставке) + сеть.
    reconfirmNewDebitUsd() {
      if (!this.order || this.order.exType !== 'BUY') return 0
      const coin = Number(this.order.buyAmount) || 0
      const newRate = Number(this.order.reconfirmRate) || 0
      const oldRate = Number(this.order.lockedPrice) || 0
      const oldFee = Number(this.order.companyFeeUsd) || 0
      const network = Number(this.order.networkFeeEstUsd) || 0
      if (!coin || !newRate) return 0
      const baseOld = coin * oldRate
      const feeRate = baseOld > 0 ? oldFee / baseOld : 0
      const baseNew = coin * newRate
      return baseNew + baseNew * feeRate + network
    },
    settlementBannerClass() {
      if (!this.settlement) return ''
      return ({
        PENDING:   'settle-pending',
        BROADCAST: 'settle-broadcast',
        CONFIRMED: 'settle-confirmed',
        FAILED:    'settle-failed',
      })[this.settlement.status] || ''
    },
    settlementIcon() {
      if (!this.settlement) return ''
      // INTERNAL settlement идёт сразу в CONFIRMED — для него своя иконка.
      if (this.settlement.settleKind === 'INTERNAL') return '✓'
      return ({
        PENDING:   '⏳',
        BROADCAST: '📤',
        CONFIRMED: '✓',
        FAILED:    '⚠',
      })[this.settlement.status] || '•'
    },
    settlementTitle() {
      if (!this.settlement) return ''
      if (this.settlement.settleKind === 'INTERNAL') {
        return 'USD начислены на ваш баланс'
      }
      if (this.settlement.settleKind === 'BANK') {
        return ({
          PENDING:   'Готовим банковский перевод',
          BROADCAST: 'Перевод отправлен в банк',
          CONFIRMED: 'Банковский перевод оформлен',
          FAILED:    'Ошибка перевода',
        })[this.settlement.status] || this.settlement.status
      }
      return ({
        PENDING:   'Готовится к отправке',
        BROADCAST: 'Транзакция в сети',
        CONFIRMED: 'Крипта получена',
        FAILED:    'Ошибка отправки',
      })[this.settlement.status] || this.settlement.status
    },
    settlementSub() {
      if (!this.settlement) return ''
      if (this.settlement.settleKind === 'INTERNAL') {
        return 'Свободные средства можно тратить прямо сейчас.'
      }
      if (this.settlement.settleKind === 'BANK') {
        return ({
          PENDING:   'Ваша крипта списана. Готовим перевод USD на ваш банковский счёт.',
          BROADCAST: 'Перевод отправлен. Зачисление обычно занимает 1–3 рабочих дня.',
          CONFIRMED: 'Крипта списана, USD отправлены на ваш счёт. Зачисление — в течение 1–3 рабочих дней.',
          FAILED:    'Свяжитесь с поддержкой — мы разберёмся с переводом вручную.',
        })[this.settlement.status] || ''
      }
      return ({
        PENDING:   'Трейдер ещё не отправил вашу крипту. Скоро появится tx_hash.',
        BROADCAST: 'Ждём N подтверждений в блокчейне.',
        CONFIRMED: 'Поступление подтверждено, крипта у вас на кошельке.',
        FAILED:    'Свяжитесь с поддержкой — мы вернём резерв вручную.',
      })[this.settlement.status] || ''
    },
    // Ссылка на blockchain-explorer по сети ордера. Когда сети нет в
    // таблице ниже — кнопку explorer'а не показываем.
    explorerUrl() {
      if (!this.settlement || !this.settlement.txHash || !this.order) return ''
      const network = (this.order.buyNetwork || '').toUpperCase()
      const hash = this.settlement.txHash
      const explorers = {
        BITCOIN:  'https://blockstream.info/tx/' + hash,
        ETHEREUM: 'https://etherscan.io/tx/' + hash,
        TRON:     'https://tronscan.org/#/transaction/' + hash,
        BSC:      'https://bscscan.com/tx/' + hash,
        SOLANA:   'https://solscan.io/tx/' + hash,
      }
      return explorers[network] || ''
    },
    ttlMinutesText() {
      // Просто пересчитываем оставшееся в "≈ N минут" для текста.
      const minutes = Math.ceil(this.remainingMs / 60000)
      if (minutes <= 1) return 'минуты'
      if (minutes < 5)  return minutes + ' минут'
      return minutes + ' мин'
    },
  },
  watch: {
    // Переход между ордерами (клик по уведомлению, будучи уже на
    // странице ордера) меняет только :orderId — Vue переиспользует
    // компонент и mounted() не срабатывает. Сбрасываем состояние и
    // грузим новый ордер заново.
    orderId(newId, oldId) {
      if (!newId || newId === oldId) return
      this.order = null
      this.settlement = null
      this.events = []
      this.loading = true
      this.fetchOrder()
    },
  },
  mounted() {
    this.fetchOrder()
    // Тикер на countdown — раз в секунду; не дёргает API, только
    // пересчитывает this.now. Чисто для секундной анимации.
    this.tickerHandle = setInterval(() => { this.now = Date.now() }, 1000)
    // Polling статуса — раз в 30 секунд. Когда придёт Phase 2 с
    // webhook'ами от trader-action, заменим. Сейчас защита от того,
    // что клиент не увидит EXPIRED → status обновится при следующем
    // тике.
    this.pollHandle = setInterval(() => { this.fetchOrder(true) }, 30 * 1000)
  },
  beforeDestroy() {
    if (this.tickerHandle) clearInterval(this.tickerHandle)
    if (this.pollHandle) clearInterval(this.pollHandle)
  },
  methods: {
    fetchOrder(quiet) {
      if (!this.orderId) {
        this.loading = false
        return
      }
      if (!quiet) this.loading = true
      axios.get(`${apiDomain}/exchange/orders/${this.orderId}`, { headers: getHeader() })
        .then((res) => { this.order = res.data })
        .catch(() => { this.order = null })
        .finally(() => { this.loading = false })
      // Таймлайн грузим параллельно — отдельный endpoint, не блокирует
      // основной order detail при ошибке.
      this.fetchEvents()
    },
    fetchEvents() {
      if (!this.orderId) return
      axios.get(`${apiDomain}/exchange/orders/${this.orderId}/events`, { headers: getHeader() })
        .then((res) => { this.events = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { /* keep prev events if any */ })
      // Settlement тянем отдельно — 204 если ещё нет строки. Используем
      // axios validateStatus, чтобы 204/200 оба не считались ошибкой.
      this.fetchSettlement()
    },
    fetchSettlement() {
      if (!this.orderId) return
      axios.get(`${apiDomain}/exchange/orders/${this.orderId}/settlement`, {
        headers: getHeader(),
        validateStatus: (s) => s === 200 || s === 204,
      })
        .then((res) => { this.settlement = res.status === 204 ? null : (res.data || null) })
        .catch(() => { this.settlement = null })
    },
    iconFor(event) {
      return ({
        CREATED:              '🆕',
        START_EXECUTION:      '⚡',
        AWAITING_RECONFIRM:   '🔄',
        RECONFIRM_ACCEPTED:   '✓',
        RECONFIRM_REJECTED:   '✕',
        RECONFIRM_EXPIRED:    '⌛',
        CANCELLED_BY_CLIENT:  '✕',
        CANCELLED_BY_ADMIN:   '⛔',
        EXPIRED_BY_TTL:       '⌛',
        LP_FILLED:                '🏷',
        SETTLEMENT_BROADCAST:     '📤',
        SETTLEMENT_BANK_TRANSFER: '🏦',
        SETTLEMENT_CONFIRMED:     '🔗',
        COMPLETED:                '🏁',
      })[event.eventType] || '•'
    },
    dotClassFor(event) {
      return ({
        CREATED:              'tl-dot-info',
        START_EXECUTION:      'tl-dot-info',
        AWAITING_RECONFIRM:   'tl-dot-warn',
        RECONFIRM_ACCEPTED:   'tl-dot-ok',
        RECONFIRM_REJECTED:   'tl-dot-no',
        RECONFIRM_EXPIRED:    'tl-dot-muted',
        CANCELLED_BY_CLIENT:  'tl-dot-muted',
        CANCELLED_BY_ADMIN:   'tl-dot-no',
        EXPIRED_BY_TTL:       'tl-dot-muted',
        LP_FILLED:                'tl-dot-info',
        SETTLEMENT_BROADCAST:     'tl-dot-info',
        SETTLEMENT_BANK_TRANSFER: 'tl-dot-info',
        SETTLEMENT_CONFIRMED:     'tl-dot-ok',
        COMPLETED:                'tl-dot-ok',
      })[event.eventType] || ''
    },
    labelFor(event) {
      return ({
        CREATED:              'Заявка создана',
        START_EXECUTION:      'Трейдер начал исполнение',
        AWAITING_RECONFIRM:   'Курс ушёл — запрошено подтверждение',
        RECONFIRM_ACCEPTED:   'Вы приняли новый курс',
        RECONFIRM_REJECTED:   'Вы отказались от нового курса',
        RECONFIRM_EXPIRED:    'Время на подтверждение истекло',
        CANCELLED_BY_CLIENT:  'Вы отменили заявку',
        CANCELLED_BY_ADMIN:   'Заявка отменена оператором',
        EXPIRED_BY_TTL:       'Заявка истекла по TTL',
        LP_FILLED:                'Сделка проведена на бирже',
        SETTLEMENT_BROADCAST:     'Крипта отправлена в сеть',
        SETTLEMENT_BANK_TRANSFER: 'Банковский перевод отправлен',
        SETTLEMENT_CONFIRMED:     'Получение подтверждено',
        COMPLETED:                'Заявка успешно закрыта',
      })[event.eventType] || event.eventType
    },
    actorLabel(event) {
      switch (event.actorType) {
        case 'CLIENT':   return 'Вы'
        case 'OPERATOR': return event.actorName || event.actorEmail || 'оператор'
        case 'SYSTEM':   return 'система автоматически'
        default:         return ''
      }
    },
    payloadLines(event) {
      const p = event.payload || {}
      const lines = []
      if (event.eventType === 'CREATED' && p.lockedPrice) {
        lines.push(`Курс: 1 ${this.cryptoSide || ''} = ${this.formatRate(p.lockedPrice)} USD`)
        if (p.sellAmount && p.sellAsset) lines.push(`Сумма: ${this.formatAmount(p.sellAmount, p.sellAsset)}`)
        if (p.buyAmount && p.buyAsset) lines.push(`К получению: ${this.formatAmount(p.buyAmount, p.buyAsset)}`)
      } else if (event.eventType === 'START_EXECUTION') {
        if (p.midAtStart != null) lines.push(`Свежий mid: ${this.formatRate(p.midAtStart)} USD`)
        if (p.driftBps != null) {
          const drift = p.driftBps / 100
          const sign = drift > 0 ? '+' : ''
          lines.push(`Дрифт: ${sign}${drift.toFixed(2)}%`)
        }
      } else if (event.eventType === 'AWAITING_RECONFIRM') {
        if (p.reconfirmRate != null) lines.push(`Новая цена: ${this.formatRate(p.reconfirmRate)} USD`)
        if (p.driftBps != null) {
          const drift = p.driftBps / 100
          const sign = drift > 0 ? '+' : ''
          lines.push(`Отклонение от исходной: ${sign}${drift.toFixed(2)}%`)
        }
      } else if (event.eventType === 'RECONFIRM_ACCEPTED') {
        if (p.prevLockedPrice && p.newLockedPrice) {
          lines.push(`Курс: ${this.formatRate(p.prevLockedPrice)} → ${this.formatRate(p.newLockedPrice)} USD`)
        }
        if (p.prevBuyAmount && p.newBuyAmount) {
          lines.push(`К получению: ${this.formatAmount(p.prevBuyAmount, '')} → ${this.formatAmount(p.newBuyAmount, '')}`.replace(/\s+$/g, ''))
        }
      } else if (event.eventType === 'EXPIRED_BY_TTL' && p.releasedAmount) {
        lines.push(`Возвращено в баланс: ${this.formatAmount(p.releasedAmount, p.sellAsset || '')}`)
      }
      if (event.note) lines.push('«' + event.note + '»')
      return lines
    },
    refresh() {
      this.fetchOrder()
    },
    downloadBankReceipt() {
      // Файл под JWT — тянем blob и открываем в новой вкладке.
      axios.get(`${apiDomain}/exchange/orders/${this.orderId}/bank-receipt`, {
        headers: getHeader(),
        responseType: 'blob',
      })
        .then((res) => {
          const url = window.URL.createObjectURL(res.data)
          window.open(url, '_blank')
          setTimeout(() => window.URL.revokeObjectURL(url), 60000)
        })
        .catch(() => {
          this.$bvToast.toast('Не удалось загрузить платёжку.', {
            variant: 'danger', title: 'Ошибка', autoHideDelay: 4000,
            toaster: 'b-toaster-top-center', solid: true,
          })
        })
    },
    submitReconfirm(accept) {
      if (this.reconfirmBusy) return
      this.reconfirmBusy = true
      axios.post(
        `${apiDomain}/exchange/orders/${this.orderId}/reconfirm`,
        { accept: accept },
        { headers: getHeader() },
      )
        .then(() => {
          this.$bvToast.toast(
            accept ? 'Курс подтверждён, заявка передана трейдеру.' : 'Заявка отменена, резерв вернётся на баланс.',
            {
              variant: accept ? 'success' : 'info',
              title: accept ? 'Подтверждено' : 'Отменено',
              autoHideDelay: 3500,
              toaster: 'b-toaster-top-center',
              solid: true,
            },
          )
          this.fetchOrder()
        })
        .catch((err) => {
          const status = err.response && err.response.status
          const body = err.response && typeof err.response.data === 'string' ? err.response.data : ''
          let msg = 'Не удалось обработать ответ. Попробуйте ещё раз.'
          if (status === 410 || body.indexOf('reconfirm-expired') !== -1) {
            msg = 'Время на ответ истекло — заявка автоматически отменена.'
          } else if (body.indexOf('insufficient-funds') !== -1) {
            msg = 'По новому курсу нужно больше средств, чем есть на балансе. Пополните счёт или отмените заявку.'
          } else if (status === 409) {
            msg = 'Статус заявки уже изменился.'
          }
          this.$bvToast.toast(msg, {
            variant: 'danger', title: 'Ошибка', autoHideDelay: 4000,
            toaster: 'b-toaster-top-center', solid: true,
          })
          this.fetchOrder()
        })
        .finally(() => { this.reconfirmBusy = false })
    },
    confirmCancel() {
      if (!confirm('Отменить заявку? Резерв вернётся на баланс.')) return
      this.cancelBusy = true
      axios.post(
        `${apiDomain}/exchange/orders/${this.orderId}/cancel`,
        {},
        { headers: getHeader() },
      )
        .then(() => {
          this.$bvToast.toast('Заявка отменена, резерв возвращён.', {
            variant: 'info', title: 'Отменено', autoHideDelay: 3500,
            toaster: 'b-toaster-top-center', solid: true,
          })
          this.fetchOrder()
        })
        .catch(() => {
          this.$bvToast.toast('Не удалось отменить заявку.', {
            variant: 'danger', title: 'Ошибка', autoHideDelay: 3500,
            toaster: 'b-toaster-top-center', solid: true,
          })
        })
        .finally(() => { this.cancelBusy = false })
    },
    formatRemaining(ms) {
      if (ms <= 0) return '00:00'
      const totalSeconds = Math.floor(ms / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      const pad = (n) => (n < 10 ? '0' + n : String(n))
      return pad(minutes) + ':' + pad(seconds)
    },
    formatAmount(value, asset) {
      const fractionDigits = asset === 'USD' ? 2 : 8
      const n = Number(value || 0)
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: asset === 'USD' ? 2 : 0,
        maximumFractionDigits: fractionDigits,
      }).format(n) + ' ' + (asset || '')
    },
    formatRate(value) {
      const n = Number(value || 0)
      // Цена в USD за единицу крипты: до 2 знаков для BTC/ETH,
      // больше для USDT-like.
      const digits = n >= 100 ? 2 : n >= 1 ? 4 : 6
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(n)
    },
    formatDateTime(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString('ru-RU', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.order-wait-page { margin-top: 18px; padding-bottom: 60px; max-width: 720px; }
.loader-block, .empty-block { text-align: center; padding: 60px 20px; color: #B1B5C3; }
.empty-ic { font-size: 40px; margin-bottom: 12px; }
.empty-title { font: 700 18px/1 Inter; color: #FCFCFD; margin-bottom: 6px; }
.empty-sub { font-size: 13px; color: #777e90; }

.status-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-radius: 16px;
  margin-bottom: 18px;
  border: 1px solid rgba(119,126,144,.2);
  background: rgba(35,38,47,.55);
}
.banner-pending { border-color: rgba(239,185,20,.45); background: rgba(239,185,20,.08); }
.banner-warn    { border-color: rgba(239,68,68,.45);  background: rgba(239,68,68,.08); }
.banner-info    { border-color: rgba(96,165,250,.45); background: rgba(96,165,250,.08); }
.banner-ok      { border-color: rgba(16,185,129,.45); background: rgba(16,185,129,.08); }
.banner-muted   { border-color: rgba(156,163,175,.35); background: rgba(156,163,175,.05); }
.banner-no      { border-color: rgba(239,68,68,.45);  background: rgba(239,68,68,.06); }

.banner-ic { font-size: 28px; flex-shrink: 0; }
.banner-content { flex: 1; }
.banner-title { color: #FCFCFD; font: 700 16px/1.2 Inter; }
.banner-sub { color: #B1B5C3; font-size: 12px; margin-top: 4px; }

.countdown-block { text-align: right; flex-shrink: 0; }
.countdown-lbl { color: #777e90; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; }
.countdown-val { color: #FCFCFD; font: 800 28px/1 Inter; letter-spacing: -0.02em; margin-top: 4px; font-variant-numeric: tabular-nums; }

.ord-card {
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  border-radius: 16px;
  padding: 18px;
}
.ord-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(119,126,144,.18);
}
.ord-id { color: #777e90; font: 700 12px/1 Inter; text-transform: uppercase; letter-spacing: .04em; }
.ord-type { color: #FCFCFD; font: 800 14px/1 Inter; }
.kv-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid rgba(119,126,144,.10);
  gap: 12px;
}
.kv-row:last-child { border-bottom: none; }
.kv-k { color: #777e90; font-size: 12px; }
.kv-v { color: #FCFCFD; font-size: 13px; font-weight: 600; text-align: right; }
.kv-v.hilite { color: #efb914; font-size: 16px; font-weight: 800; }
.kv-hint {
  color: #777e90;
  font-size: 11px;
  line-height: 1.5;
  padding: 4px 0 8px;
  border-bottom: 1px solid rgba(119,126,144,.10);
}

.note {
  margin-top: 14px;
  padding: 12px 14px;
  background: rgba(96,165,250,.08);
  border: 1px solid rgba(96,165,250,.28);
  color: #b1b5c3;
  border-radius: 12px;
  font-size: 12.5px;
  line-height: 1.5;
}

.actions { display: flex; justify-content: flex-end; margin-top: 14px; }
.cta-btn {
  background: #efb914;
  border-color: #efb914;
  color: #1a1d22;
  font-weight: 700;
  border-radius: 12px;
}
.cta-btn:hover { filter: brightness(1.05); }

.reconfirm-card {
  margin-top: 14px;
  padding: 18px 20px;
  background: rgba(239,68,68,.10);
  border: 1px solid rgba(239,68,68,.45);
  border-radius: 16px;
}
.reconfirm-title {
  color: #FCFCFD;
  font: 800 18px/1.2 Inter;
  letter-spacing: -0.01em;
  margin-bottom: 6px;
}
.reconfirm-sub {
  color: #B1B5C3;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 14px;
}
.rate-compare {
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 14px;
}
.rate-box {
  flex: 1;
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  border-radius: 12px;
  padding: 12px 14px;
}
.rate-box.rate-old { opacity: 0.65; }
.rate-box.rate-new { border-color: rgba(239,185,20,.55); }
.rate-lbl { font-size: 10px; color: #777e90; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px; }
.rate-val { color: #FCFCFD; font: 800 20px/1 Inter; font-variant-numeric: tabular-nums; }
.rate-sub { font-size: 11px; color: #777e90; margin-top: 4px; }
.rate-arrow {
  display: flex;
  align-items: center;
  font-size: 22px;
  color: #777e90;
  padding: 0 4px;
}
.reconfirm-note-line {
  font-size: 12.5px;
  color: #FCFCFD;
  background: rgba(96,165,250,.08);
  border: 1px solid rgba(96,165,250,.28);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 12px;
  line-height: 1.5;
}
.reconfirm-countdown { font-size: 12px; color: #B1B5C3; margin-bottom: 12px; }
.reconfirm-countdown.warn { color: #F87171; }
.reconfirm-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.accept-btn { flex: 1; max-width: 280px; }
.reject-btn { border-radius: 12px; }

.timeline-card {
  margin-top: 14px;
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  border-radius: 16px;
  padding: 16px 18px;
}
.timeline-title {
  color: #FCFCFD;
  font: 700 14px/1 Inter;
  margin-bottom: 12px;
}
.timeline-list { display: flex; flex-direction: column; gap: 12px; }
.timeline-item { display: flex; gap: 12px; }
.timeline-dot {
  width: 32px; height: 32px; flex-shrink: 0;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px;
  border: 1px solid rgba(119,126,144,.3);
  background: rgba(35,38,47,.75);
}
.tl-dot-info  { border-color: rgba(96,165,250,.55); background: rgba(96,165,250,.10); }
.tl-dot-ok    { border-color: rgba(16,185,129,.55); background: rgba(16,185,129,.10); }
.tl-dot-warn  { border-color: rgba(239,185,20,.55); background: rgba(239,185,20,.10); }
.tl-dot-no    { border-color: rgba(239,68,68,.55); background: rgba(239,68,68,.10); }
.tl-dot-muted { border-color: rgba(156,163,175,.4); background: rgba(156,163,175,.06); }

.timeline-body { flex: 1; min-width: 0; }
.timeline-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.timeline-label { color: #FCFCFD; font: 700 12.5px/1.3 Inter; }
.timeline-time { color: #777e90; font-size: 11px; font-variant-numeric: tabular-nums; }
.timeline-actor { color: #B1B5C3; font-size: 11px; margin-top: 2px; }
.timeline-payload { color: #777e90; font-size: 11.5px; margin-top: 4px; line-height: 1.5; }

.settlement-card {
  margin-top: 14px;
  padding: 16px 18px;
  border-radius: 16px;
  border: 1px solid rgba(119,126,144,.2);
  background: rgba(35,38,47,.55);
}
.settle-pending   { border-color: rgba(239,185,20,.45); background: rgba(239,185,20,.08); }
.settle-broadcast { border-color: rgba(96,165,250,.45); background: rgba(96,165,250,.08); }
.settle-confirmed { border-color: rgba(16,185,129,.45); background: rgba(16,185,129,.08); }
.settle-failed    { border-color: rgba(239,68,68,.45); background: rgba(239,68,68,.08); }

.settlement-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(119,126,144,.15);
}
.settlement-ic { font-size: 24px; flex-shrink: 0; }
.settlement-titles { flex: 1; }
.settlement-title { color: #FCFCFD; font: 700 14px/1.2 Inter; }
.settlement-sub { color: #B1B5C3; font-size: 11.5px; margin-top: 3px; }

.explorer-link {
  margin-left: 10px;
  font-size: 10.5px;
  color: #efb914;
  text-transform: uppercase;
  letter-spacing: .04em;
  font-weight: 700;
}
.explorer-link:hover { text-decoration: underline; }

.settlement-cta-row {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}
</style>
